import { useState } from 'react';
import MapPortalService from '../services/MapPortal/mapportal.service';
import { useMutation } from '@tanstack/react-query';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { waitForJobEvent } from '../services/socket.service';

// Backstop only — the `export:status` socket event normally ends the wait first.
const EXPORT_POLL_FALLBACK_MS = 20000;

export type ExportFormat = 'shapefile' | 'csv' | 'kml' | 'geojson';
type ExportSelection = {
  filter?: { search?: string; filters?: { field: string; op: string; value: unknown }[] };
  featureIds?: string[];
  /** Query Tool match predicate (sift filter). */
  matchFilter?: Record<string, any>;
  matchExtent?: { type: 'Polygon'; coordinates: number[][][] } | null;
  /** Proximity Tool match predicate. */
  proximityMatch?: Record<string, any> | null;
  /** Match rows the user unchecked. */
  excludedFeatureIds?: string[];
  /** Non-match rows the user manually added to the result. */
  includedFeatureIds?: string[];
  /** Reverse selection: export the complement of the forward match set. */
  matchInvert?: boolean;
};

type ExportOptions = {
  /** User-facing name for the downloaded file. Data is still resolved by layerName. */
  fileName?: string;
};

type UseDownloadProps = {
  customFailureMessage?: string;
  customSuccessMessage?: string;
};

const useDownload = ({ customFailureMessage = 'Export Failed', customSuccessMessage = 'Download Ready' }: UseDownloadProps = {}) => {
  const { downloadLayer, downloadSelectedFeatures, requestFeatureExport, getFeatureExportStatus } = MapPortalService();
  const { mutateAsync: downloadLayerAsync, isPending: isDownloadingLayer } = useMutation(downloadLayer());
  const { mutateAsync: downloadSelectedFeaturesAsync, isPending: isDownloadingSelected } = useMutation(downloadSelectedFeatures());

  const [activeExportSelectedCount, setActiveExportSelectedCount] = useState(0);
  const [activeExportLayerCount, setActiveExportLayerCount] = useState(0);
  const isExportingSelected = activeExportSelectedCount > 0;
  const isExportingLayer = activeExportLayerCount > 0;

  /**
   * Kick off a browser download without navigating the current document.
   *
   * Cross-origin S3 presigned URLs ignore the `download` attribute, so a bare
   * `<a>.click()` can be treated as a top-level navigation. That fires
   * `pagehide`/`beforeunload` on the preview page, which tears down the temp
   * PostGIS `preview_*` table — first export works, every subsequent one fails
   * with "relation does not exist". A hidden iframe keeps the parent document
   * put; S3 already sends `Content-Disposition: attachment`.
   */
  const triggerBrowserDownload = (url: string, _filename?: string) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = url;
    document.body.appendChild(iframe);
    // Allow the browser to start the transfer, then drop the frame. 60s covers
    // slow starts without leaking iframes if the user exports repeatedly.
    window.setTimeout(() => {
      iframe.remove();
    }, 60_000);
  };

  /**
   * Download at scale: enqueue a server-side ogr2ogr export (PostGIS → S3) and
   * poll until the signed URL is ready, then download. Handles millions of
   * features where the synchronous WFS download times out. Selection = predicate,
   * gid list, or whole layer (omit selection).
   *
   * NOTE: polling is a `while` loop inside this single async call (not a
   * self-rescheduling `setTimeout` chain) so the whole operation — including
   * every poll — stays inside one try/catch/finally. That's what makes the
   * relevant counter (`activeExportSelectedCount` or `activeExportLayerCount`,
   * and therefore `isExportingSelected` / `isExportingLayer`) accurate for the
   * full duration, and what guarantees a status-check failure mid-poll is
   * actually caught instead of becoming an unhandled rejection.
   *
   * The backend pushes `export:status` to the user's socket room when the job
   * lands, so each wait is a race between that event and a slow backstop sleep.
   * The loop shape — and everything it guarantees above — is unchanged; only
   * how long we wait between checks is.
   */
  const handleAsyncExport = async (
    layerName: string,
    format: ExportFormat,
    selection: ExportSelection = {},
    options: ExportOptions = {},
  ): Promise<void> => {
    // A real selection (predicate or gid list) counts as "export selected";
    // an empty selection ({}) is a whole-layer export.
    const hasMatchPred =
      Boolean(selection.proximityMatch) ||
      Boolean(selection.matchFilter && Object.keys(selection.matchFilter).length) ||
      Boolean(selection.matchExtent);
    const isSelectedExport = Boolean(selection.filter) || Boolean(selection.featureIds?.length) || hasMatchPred;
    const setCount = isSelectedExport ? setActiveExportSelectedCount : setActiveExportLayerCount;

    setCount((c) => c + 1);
    try {
      Notification({ message: 'Preparing export…', type: NotificationTypes.INFO });
      const { jobId } = await requestFeatureExport({
        layerName,
        format,
        ...(options.fileName?.trim() ? { fileName: options.fileName.trim() } : {}),
        ...(hasMatchPred
          ? {
              ...(selection.matchFilter ? { matchFilter: selection.matchFilter } : {}),
              ...(selection.matchExtent ? { matchExtent: selection.matchExtent } : {}),
              ...(selection.proximityMatch ? { proximityMatch: selection.proximityMatch } : {}),
              ...(selection.excludedFeatureIds?.length ? { excludedFeatureIds: selection.excludedFeatureIds } : {}),
              ...(selection.includedFeatureIds?.length ? { includedFeatureIds: selection.includedFeatureIds } : {}),
              ...(selection.matchInvert ? { matchInvert: true } : {}),
            }
          : selection.filter
            ? { filter: selection.filter }
            : selection.featureIds?.length
              ? { featureIds: selection.featureIds }
              : {}),
      });

      const startedAt = Date.now();
      const MAX_MS = 20 * 60 * 1000; // 20 min ceiling

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const s = await getFeatureExportStatus(jobId);

        if (s.status === 'completed' && s.url) {
          triggerBrowserDownload(s.url, s.filename);
          Notification({ message: customSuccessMessage, type: NotificationTypes.SUCCESS });
          return;
        }
        if (s.status === 'failed' || s.status === 'not_found') {
          Notification({ message: 'Export failed', description: s.error, type: NotificationTypes.ERROR });
          return;
        }
        if (Date.now() - startedAt > MAX_MS) {
          Notification({ message: 'Export is taking too long — try a smaller selection.', type: NotificationTypes.ERROR });
          return;
        }

        // Wake on the socket event, or on the backstop timer if it never arrives.
        const waiter = waitForJobEvent('export:status', jobId);
        try {
          await Promise.race([new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_FALLBACK_MS)), waiter.promise]);
        } finally {
          waiter.cancel();
        }
      }
    } catch {
      Notification({ message: customFailureMessage, type: NotificationTypes.ERROR });
    } finally {
      // Always runs — success, every explicit failure branch, timeout, and
      // thrown error alike — so the counter can never get stuck open.
      setCount((c) => c - 1);
    }
  };

  const handleDownload = async (layerName: string, format: string) => {
    try {
      const data = await downloadLayerAsync({ layerName, format });

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${layerName}.${format === 'shapefile' ? 'zip' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      Notification({ message: 'Download failed', type: NotificationTypes.ERROR });
    }
  };

  const handleDownloadSelected = async (
    layerName: string,
    format: string,
    featureIds: string[],
    // P3 — when a predicate ("all matching filter") is given, the server exports
    // via CQL_FILTER and the gid list is ignored (so 50k selections work).
    filter?: { search?: string; filters?: { field: string; op: string; value: unknown }[] },
  ) => {
    try {
      const data = await downloadSelectedFeaturesAsync({
        layerName,
        format,
        ...(filter ? { filter } : { featureIds }),
      });

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${layerName}_selected.${format === 'shapefile' ? 'zip' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      Notification({ message: 'Download failed', type: NotificationTypes.ERROR });
    }
  };

  return {
    handleDownload,
    handleDownloadSelected,
    handleAsyncExport,
    isDownloadingLayer,
    isDownloadingSelected,
    isExportingLayer,
    isExportingSelected,
    isLayerDownloadInProgress: isDownloadingLayer || isExportingLayer,
    isSelectedDownloadInProgress: isDownloadingSelected || isExportingSelected,
  };
};

export default useDownload;
